import React, { useEffect } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { projectNameScheme, stringScheme } from 'src/utils/validation-schemes';
import { useUpdateProjectMutation } from 'src/services';
import { Button } from 'src/components/ui/button';
import Input from 'src/components/Input/Input';

export const ChangeProjectNameModalContent = ({ project: currentProject, close = () => {} }) => {
  const notification = useNotification();

  const [
    updateProject,
    {
      isLoading: isLoadingUpdateProject,
      isError: isUpdateProjectError,
      isSuccess: isUpdateProjectSuccess,
    },
  ] = useUpdateProjectMutation();

  const projectNameDefault = currentProject?.name;
  const {
    handleSubmit,
    control,
    setValue,
    resetField,
    watch,
    clearErrors,
    formState: { isDirty, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      projectName: projectNameDefault || '',
    },
    resolver: yupResolver(
      object({
        projectName: projectNameScheme({ required: true }).label('New name'),
      }),
    ),
  });

  const nameWatch = watch('projectName');

  const submit = () => {
    updateProject({
      cocbi_project_id: currentProject.cocbi_project_id,
      use_cocbi_model: currentProject.use_cocbi_model,
      projectId: currentProject.id,
      name: nameWatch.trim(),
      ai_reply_generation: currentProject.ai_reply_generation,
      ai_text_lense: currentProject.ai_text_lense,
    }).then(() => {
      resetField('projectName', { defaultValue: nameWatch.trim() });
    });
  };

  useEffect(() => {
    if (isUpdateProjectError) {
      resetField('projectName', { defaultValue: projectNameDefault });
    }
  }, [isUpdateProjectError]);

  useEffect(() => {
    if (currentProject) {
      setValue('projectName', currentProject?.name);
    }
  }, [currentProject]);

  useEffect(() => {
    if (isUpdateProjectSuccess) {
      notification.success('Project name has been updated!');
      close();
    }
  }, [isUpdateProjectSuccess]);

  const isLoading = isLoadingUpdateProject;

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="flex flex-col">
        <Controller
          control={control}
          name="projectName"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Name'}
              placeholder={'Acme AI Agent'}
              labelClassName={'pb-2 font-medium opacity-50 text-sm font-jamjuree'}
              className="!py-2.5 !px-3.5"
              autoFocus
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={onChange}
              onFocus={() => {
                clearErrors('projectName');
              }}
            />
          )}
        />
      </div>
      <div className="sticky bottom-0">
        <div className="flex w-full items-center justify-center gap-2.5 pt-5 bg-white">
          <Button
            type="button"
            variant="outline"
            className="transparent black min-w-28"
            onClick={() => close()}
          >
            Cancel
          </Button>
          <Button
            disabled={!isDirty || !isValid || isLoading}
            type="submit"
            className="min-w-28"
            onClick={submit}
          >
            {isLoading ? 'Loading...' : 'Rename'}
          </Button>
        </div>
      </div>
    </form>
  );
};
